import {
    MAX_SUGGESTION_BLOCK_CHARS,
    MAX_SUGGESTION_BLOCKS,
    MAX_SUGGESTION_READONLY_CHARS,
    MAX_SUGGESTION_TOTAL_TEXT_CHARS
} from "../../../../common/aiConstants";
import { AuthoringBlockType } from "../../../../common/constants";
import getObjectHash from "../../../../common/utils/getObjectHash";
import { htmlToText } from "../../../core/utilities/htmlTextHelpers";

/**
 * Block ids that TextElement synthesizes for values that have no backing model entry (see the
 * `textModel` getter and friends in js/canvas/v10/elements/base/Text/TextElement.js). Writing to
 * one of these is a silent no-op, so they must never become a rewrite target.
 */
const SYNTHETIC_BLOCK_IDS = ["html", "dataSource", "defaultValue", "faux"];

function truncate(text, maxChars) {
    if (text.length <= maxChars) {
        return text;
    }
    if (maxChars <= 1) {
        return text.slice(0, maxChars);
    }
    return `${text.slice(0, maxChars - 1)}…`;
}

function getTextElements(canvas) {
    // `useIsInstanceOf` is required: element.type is the constructor name, so plain matching misses
    // real editable text like TextListColumnHeader, BigNumberValue and VennDiagramLabel. The cost is
    // that getStageElementsByType pushes ElementTextBlock/ElementAttribution twice (once via
    // isInstanceOf, once via its own special case), hence the dedupe by uniquePath.
    const seenPaths = new Set();

    return (canvas.getStageElementsByType("TextElement", true) ?? []).filter(element => {
        if (!element || !element.canEdit) {
            return false;
        }
        if (seenPaths.has(element.uniquePath)) {
            return false;
        }
        seenPaths.add(element.uniquePath);
        return true;
    });
}

function getNeighborTitles(slide) {
    const presentation = slide?.presentation;
    if (!presentation || typeof slide.getIndex !== "function") {
        return [];
    }

    try {
        const slides = presentation.slides ?? [];
        const index = slide.getIndex();

        return [slides[index - 1], slides[index + 1]]
            .filter(Boolean)
            .map(neighbor => neighbor.getMetadata?.()?.title ?? neighbor.get?.("metadata")?.title ?? "")
            .map(title => (title ?? "").toString().trim())
            .filter(Boolean);
    } catch (err) {
        return [];
    }
}

function getReadOnlyText(canvas) {
    const readOnlyText = [];
    let remaining = MAX_SUGGESTION_READONLY_CHARS;

    // Tables carry no block ids — their text lives at cells[i].model.cellText.text — so they are
    // context only. That is still what lets the model say "this table is really three numeric
    // series, make it a chart".
    for (const table of canvas.getStageElementsByType("Table") ?? []) {
        if (remaining <= 0) {
            break;
        }

        const cellText = (table.cells ?? [])
            .map(cell => (cell?.model?.cellText?.text ?? "").toString().trim())
            .filter(Boolean)
            .join(" | ");

        if (!cellText) {
            continue;
        }

        const text = truncate(cellText, remaining);
        remaining -= text.length;

        readOnlyText.push({
            elementPath: table.uniquePath,
            elementType: "Table",
            label: "table",
            text
        });
    }

    return readOnlyText;
}

/**
 * Builds a block-ref-keyed snapshot of a slide for the suggestions endpoint.
 *
 * Deliberately separate from SlideAiController.buildPromptFromSlide: that produces lossy markdown for
 * a *generation* prompt and is consumed by two dialogs, and it fundamentally cannot carry block
 * identity. Here every editable block gets a short ref ("b1", "b2", ...) that goes on the wire, while
 * the uuid and element path stay client-side in `blocksByRef`.
 */
export function buildSlideInventory(canvas) {
    const slide = canvas?.slide;
    const metadata = slide?.getMetadata?.() ?? {};

    const textBlocks = [];
    const blocksByRef = new Map();
    const elementTypes = new Set();
    let totalChars = 0;

    for (const element of getTextElements(canvas)) {
        elementTypes.add(element.type);

        const blocks = element.textModel?.blocks ?? [];
        for (const block of blocks) {
            if (textBlocks.length >= MAX_SUGGESTION_BLOCKS || totalChars >= MAX_SUGGESTION_TOTAL_TEXT_CHARS) {
                break;
            }
            if (block?.type !== AuthoringBlockType.TEXT) {
                continue;
            }
            if (!block.id || SYNTHETIC_BLOCK_IDS.includes(block.id)) {
                continue;
            }

            const text = htmlToText(block.html ?? "").trim();
            if (!text) {
                continue;
            }

            const ref = `b${textBlocks.length + 1}`;
            const truncated = truncate(
                text,
                Math.min(MAX_SUGGESTION_BLOCK_CHARS, MAX_SUGGESTION_TOTAL_TEXT_CHARS - totalChars)
            );
            totalChars += truncated.length;

            textBlocks.push({
                ref,
                elementRole: element.uniquePath?.split("/")[1] ?? "other",
                textStyle: block.textStyle,
                text: truncated
            });

            blocksByRef.set(ref, {
                blockId: block.id,
                elementPath: element.uniquePath,
                text: truncated,
                // A rewrite generated from only the opening 400 characters would replace and lose
                // the unseen remainder. Keep long blocks as context, but never resolve a rewrite
                // action for them.
                isTruncated: truncated !== text
            });
        }
    }

    // The primary element and its immediate children are the signal for "what kind of slide is this
    // already", which is what stops the model suggesting a conversion to a type already on screen.
    // A full recursive walk isn't worth it — every element is a BaseElement, so it would return the
    // whole tree.
    const primary = canvas?.getPrimaryElement?.();
    if (primary?.type) {
        elementTypes.add(primary.type);
        for (const child of Object.values(primary.elements ?? {})) {
            if (child?.type) {
                elementTypes.add(child.type);
            }
        }
    }

    return {
        templateId: canvas?.model?.template_id ?? null,
        slideCategory: metadata.slideCategory ?? null,
        elementTypes: [...elementTypes].sort(),
        hasSlideImage: !!metadata.generatedModel?.slideImage,
        presentationTopic: slide?.presentation?.get?.("metadata")?.topic ?? "",
        neighborTitles: getNeighborTitles(slide),
        textBlocks,
        readOnlyText: getReadOnlyText(canvas),
        blocksByRef
    };
}

/**
 * The cost model lives here. Only content-bearing fields are hashed, so restyling, moving,
 * recoloring or animating a slide is free while changing a word is not.
 *
 * `sortArrays: false` is required — reordering bullets IS a content change worth re-reviewing, and
 * getObjectHash's default of sorting arrays would hide it.
 */
export function getInventoryHash(inventory) {
    return getObjectHash({
        templateId: inventory.templateId,
        elementTypes: inventory.elementTypes,
        hasSlideImage: inventory.hasSlideImage,
        blocks: inventory.textBlocks.map(block => [block.ref, block.textStyle, block.text]),
        readOnly: inventory.readOnlyText.map(entry => [entry.elementPath, entry.text])
    }, false, false);
}

/**
 * Strips the client-only fields so the request carries refs but no uuids or element paths.
 */
export function getInventoryRequestPayload(inventory) {
    return {
        templateId: inventory.templateId ?? undefined,
        slideCategory: inventory.slideCategory ?? undefined,
        elementTypes: inventory.elementTypes,
        hasSlideImage: inventory.hasSlideImage,
        presentationTopic: inventory.presentationTopic || undefined,
        neighborTitles: inventory.neighborTitles,
        textBlocks: inventory.textBlocks,
        readOnlyText: inventory.readOnlyText
    };
}

/**
 * Resolves a persisted {elementPath, blockId} back to a live element, with validation.
 *
 * canvas.getElementByUniquePath walks getChild(), which silently falls back to the FIRST child when
 * an id is missing (BaseElement.getChild). A stale path therefore returns some other element rather
 * than throwing, so trusting it would mean rewriting the wrong text.
 */
export function resolveTextElement(canvas, elementPath, blockId) {
    let element;
    try {
        element = canvas.getElementByUniquePath(elementPath);
    } catch (err) {
        return null;
    }

    if (!element) {
        return null;
    }
    if (element.uniquePath !== elementPath) {
        return null;
    }
    if (!element.canEdit) {
        return null;
    }
    if (typeof element.getBlockById !== "function") {
        return null;
    }
    if (!element.getBlockById(blockId)) {
        return null;
    }

    return element;
}
